/**
 * Requisições de Compra (Suprimentos) — a fila do comprador, ordenada por
 * prazo (ADR-0069, 2026-09-14).
 *
 * Uma tela só para TODAS as compras (terceiros, ferragem em falta, lote,
 * PharuCut e manual), separadas pelo filtro de ORIGEM. KPIs acionáveis,
 * busca com atalho "/", chips de status, quatro vistas (tabela / kanban sem
 * scroll lateral / linha do tempo = auditoria / calendário de prazos),
 * drawer de detalhe, seleção em massa e painéis (filtros, colunas, nova).
 *
 * Nenhum arraste é bloqueado: o backend resolve o destino e, quando falta
 * motivo ou OC, responde 409 `{precisa}` e a tela abre o modal certo.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiCalendar, FiClock, FiColumns, FiList, FiPlus, FiSearch, FiSliders, FiTrash2, FiX,
} from "react-icons/fi";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import ConfirmDialog from "../../../components/ConfirmDialog";
import EntityHeader from "../../../components/EntityHeader";
import ExportMenu from "../../../components/ExportMenu";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { useRespostaMaisRecente, useValorComAtraso } from "../../../hooks/useBuscaRemota";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { usePersistedFilters } from "../../../hooks/usePersistedFilters";
import { PageContainer } from "../../../styles/shared";
import { resumoSolicitantes } from "../../../services/rqc";
import { formatarDataLocal } from "../../../utils/dinheiro";
import {
  ORIGEM_LABEL, ORIGEM_TONE, TIPO_TERCEIRO_LABEL,
  dadosParaExportacao, eventosRequisicoes, listarRequisicoes, loteRequisicoes, marcarSituacaoItem, moverEtapaRequisicao,
  moverRequisicaoPara, obterColunasKanbanCompras, obterRequisicao, pessoasCompras, resumoRequisicoes,
  salvarColunasKanbanCompras,
} from "../../../services/compras";
import {
  COLUNAS_PADRAO, SITUACAO_LABEL, SITUACAO_TONE, SITUACOES_COMPRA, rotuloPrazo,
} from "../../../services/requisicoesCompraUi";
import { getToneTokens } from "../../../styles/shared";
import { Carregando, Vazio, VazioFiltro } from "../_shared/Estados";
import Calendario from "../_shared/quadro/Calendario";
import ColunasPanel from "../_shared/quadro/ColunasPanel";
import Kanban from "../_shared/quadro/Kanban";
import {
  Badge, BatchBar, Chip, FilterBar, IconBtn, KMeta, KNum, KTitle, Mono, SearchInput, SearchWrap, ToggleBtn, ViewToggle,
} from "../_shared/quadro/styles";
import Detalhe from "./Detalhe";
import FiltrosPanel, { FILTROS_PADRAO, contarFiltrosAtivos } from "./FiltrosPanel";
import GerarOcPanel from "./GerarOcPanel";
import JuntarModal from "./JuntarModal";
import KpisFila from "./KpisFila";
import LinhaEventos from "./LinhaEventos";
import NovaRequisicao from "./NovaRequisicao";
import { fatiarParaPdf, idsDasLinhas, montarExportacaoCompleta } from "./exportacaoCompleta";
import TabelaComprador from "./TabelaComprador";
import { passaNosFiltrosDaFila } from "./filtrosDaFila";
import { destinoDaColuna, precisaAntesDeMover } from "./transicoes";
import { Avatar, BarraItens, ModalMotivo, StatusPill, TagOrigem } from "./ui";

/** Vocabulário que os componentes compartilhados de quadro recebem por prop. */
const VOCAB = { SITUACAO_LABEL, SITUACAO_TONE, ORIGEM_LABEL, ORIGEM_TONE };

const VISTAS = [
  { id: "tabela", label: "Tabela", icone: FiList },
  { id: "kanban", label: "Kanban", icone: FiColumns },
  { id: "linha", label: "Linha do tempo", icone: FiClock },
  { id: "calendario", label: "Calendário", icone: FiCalendar },
];

const KPI_PARA_FILTRO = {
  atrasadas: { soAtrasadas: true },
  aguardando: { situacao: "AGUARDANDO_APROVACAO" },
  sem_anexo: { semAnexo: true },
  entregas: { situacao: "AGUARDANDO_ENTREGA" },
};

const LEGENDA_CAL = [
  { label: "Prazo de cotação", tone: "warning" },
  { label: "Entrega prevista", tone: "teal" },
  { label: "Atrasada", tone: "danger" },
];

function eventosDoCalendario(r) {
  const evs = [];
  if (r.prazo_cotacao && !["ATENDIDA", "CANCELADA"].includes(r.situacao)) {
    evs.push({ data: r.prazo_cotacao, tone: r.atrasada ? "danger" : "warning", titulo: r.atrasada ? "atrasada" : "prazo de cotação" });
  }
  if (r.previsao_entrega) evs.push({ data: r.previsao_entrega, tone: "teal", titulo: "entrega prevista" });
  return evs;
}

function TEXTO_MODAL(destino, req) {
  const base = {
    CANCELADA: { titulo: "Cancelar requisição", descricao: "Os itens abertos serão cancelados. Informe o motivo.", tone: "danger", confirmarLabel: "Cancelar requisição" },
    RASCUNHO: { titulo: "Voltar para 'A cotar'", descricao: "Os itens fechados por fora voltam a ficar pendentes.", confirmarLabel: "Voltar" },
    EM_COTACAO: { titulo: "Voltar para 'Em andamento'", descricao: "A requisição volta a ser cotada.", confirmarLabel: "Voltar" },
    APROVADA: { titulo: "Reabrir como aprovada", descricao: "A requisição volta à fila do comprador.", confirmarLabel: "Reabrir" },
    ATENDIDA: { titulo: req?.situacao === "AGUARDANDO_ENTREGA" ? "Confirmar recebimento" : "Dar por recebida", descricao: req?.situacao === "AGUARDANDO_ENTREGA" ? "Registra a entrada do saldo pendente de cada OC no estoque." : "Sem ordem de compra: a requisição fecha sem fornecedor nem valor.", rotulo: "Observação", obrigatorio: false, confirmarLabel: "Confirmar" },
  };
  return base[destino] || { titulo: "Confirmar", confirmarLabel: "Confirmar" };
}

export default function RequisicoesCompra() {
  const { request, isMaster, hasAllPermissions, usuario } = useAuth();
  const toast = useToast();
  const podeGerir = isMaster || hasAllPermissions(["compras:criar_requisicao"]);
  const podeAprovar = isMaster || hasAllPermissions(["compras:aprovar_requisicao"]);
  const podeConsolidar = isMaster || hasAllPermissions(["compras:consolidar_requisicao"]);
  const mobile = useMediaQuery("(max-width: 480px)");
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Vindo do Cadastro de Suprimentos ("Criar requisição" na sugestão de compra).
  const [novaInicial, setNovaInicial] = useState(null);

  const [todas, setTodas] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(false);
  const [buscaInput, setBuscaInput] = useState("");
  const { filtros: persistidos, setFiltros: setPersistidos } = usePersistedFilters("req-compra:fila", { vista: "tabela" });
  const vista = VISTAS.some((v) => v.id === persistidos.vista) ? persistidos.vista : "tabela";
  const setVista = (v) => setPersistidos((p) => ({ ...p, vista: v }));
  const [filtros, setFiltros] = useState(() => ({ ...FILTROS_PADRAO }));
  const [kpiAtivo, setKpiAtivo] = useState(null);
  const [selecionadas, setSelecionadas] = useState(() => new Set());
  const [juntarOpen, setJuntarOpen] = useState(false);
  // O que a tabela mostra agora (visão, colunas e linhas): é o que se exporta.
  const tabelaNaTela = useRef(null);
  const guardarTabela = useCallback((t) => { tabelaNaTela.current = t; }, []);
  // Muda a cada carga da fila: a visão por item relê os itens.
  const [versaoDaFila, setVersaoDaFila] = useState(0);
  const [colunas, setColunas] = useState([]);
  const [colunasOpen, setColunasOpen] = useState(false);
  const [filtrosOpen, setFiltrosOpen] = useState(false);
  const [novaOpen, setNovaOpen] = useState(false);
  const [detalheId, setDetalheId] = useState(null);
  const [detalheReq, setDetalheReq] = useState(null);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const [pessoas, setPessoas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [eventosLoading, setEventosLoading] = useState(false);
  const [tipoEvento, setTipoEvento] = useState(null);
  const [modal, setModal] = useState(null); // {req, destino, precisa} | {lote: "cancelar"}
  const [confirmExcluir, setConfirmExcluir] = useState(false);
  const [gerarOc, setGerarOc] = useState(null); // {req, itens}
  const [exportando, setExportando] = useState(false);
  const buscaRef = useRef(null);

  // Ref para o toast: dá acesso ao avisador de dentro de callbacks memoizados
  // sem colocá-lo no array de dependências (ver comentário em `carregar`).
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const busca = useValorComAtraso(buscaInput.trim());
  const iniciarCarga = useRespostaMaisRecente();

  // Atalho "/" foca a busca.
  useEffect(() => {
    function onKey(e) {
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        buscaRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ATENÇÃO ao mexer nas dependências: `useToast()` devolve um OBJETO NOVO a
  // cada render. Colocar `toast` aqui recria `carregar` a cada render e vira
  // tempestade de requisições (produção, 2026-08-04). Só `request` e `busca`.
  const carregar = useCallback(async () => {
    const vale = iniciarCarga();
    try {
      // Carrega TUDO e filtra no cliente: o kanban precisa de todos os status
      // para montar as raias, e os chips contam o conjunto inteiro.
      const r = await listarRequisicoes(request, { busca });
      // Resposta de uma busca já superada não pode vencer a mais recente.
      if (!vale()) return;
      setTodas(Array.isArray(r) ? r : r?.results || []);
      setVersaoDaFila((v) => v + 1);
      setErroCarga(false);
    } catch (err) {
      if (!vale()) return;
      setErroCarga(true);
      toastRef.current.error(err?.payload?.detalhe || err?.message || "Falha ao carregar.");
    } finally {
      if (vale()) setLoading(false);
    }
    resumoRequisicoes(request).then((r) => setResumo(r || null)).catch(() => {});
  }, [request, busca, iniciarCarga]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    obterColunasKanbanCompras(request)
      .then((r) => setColunas(r?.colunas || []))
      .catch(() => setColunas([]));
    pessoasCompras(request).then((r) => setPessoas(r?.pessoas || [])).catch(() => setPessoas([]));
  }, [request]);

  const carregarEventos = useCallback(async () => {
    setEventosLoading(true);
    try {
      const r = await eventosRequisicoes(request, { limite: 400 });
      setEventos(r?.eventos || []);
    } catch {
      setEventos([]);
    } finally {
      setEventosLoading(false);
    }
  }, [request]);

  useEffect(() => { if (vista === "linha") carregarEventos(); }, [vista, carregarEventos]);

  useEffect(() => {
    const pedido = location.state?.novaRequisicao;
    if (!pedido) return;
    setNovaInicial(pedido);
    setNovaOpen(true);
    // Limpa o state para um F5 não reabrir o painel.
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Deep-link: ?abrir=<id> abre o drawer (usado pelo sino e por links).
  useEffect(() => {
    const id = searchParams.get("abrir");
    if (id && id !== detalheId) abrirDetalhe(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ---------------------------------------------------------- filtragem
  const filtradas = useMemo(
    () => todas.filter((r) => passaNosFiltrosDaFila(r, filtros)),
    [todas, filtros],
  );

  const contagens = useMemo(() => {
    const c = { ativas: 0, canceladas: 0 };
    todas.forEach((r) => {
      c[r.situacao] = (c[r.situacao] || 0) + 1;
      if (r.situacao === "CANCELADA") c.canceladas += 1; else c.ativas += 1;
    });
    return c;
  }, [todas]);

  const vencemHoje = useMemo(() => filtradas.filter((r) => r.dias_para_prazo === 0), [filtradas]);
  // Solicitantes vem da propria fila (quem pede pode ser de qualquer setor).
  const solicitantes = useMemo(() => {
    const m = new Map();
    todas.forEach((r) => { if (r.solicitado_por && !m.has(r.solicitado_por)) m.set(r.solicitado_por, { id: r.solicitado_por, nome: r.solicitado_por_nome || "—" }); });
    return Array.from(m.values()).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [todas]);
  const nFiltros = contarFiltrosAtivos(filtros);
  const chipsAtivos = [
    ...(filtros.origem || []).map((o) => ({ id: `origem:${o}`, texto: `Origem: ${ORIGEM_LABEL[o] || o}`, remover: () => setFiltros((f) => ({ ...f, origem: f.origem.filter((x) => x !== o) })) })),
    ...(filtros.comprador || []).map((c) => ({ id: `comp:${c}`, texto: `Comprador: ${c === "nenhum" ? "sem responsável" : (pessoas.find((p) => p.id === c)?.nome?.split(" ")[0] || "—")}`, remover: () => setFiltros((f) => ({ ...f, comprador: f.comprador.filter((x) => x !== c) })) })),
    ...(filtros.solicitante || []).map((c) => ({ id: `sol:${c}`, texto: `Solicitante: ${(solicitantes.find((p) => p.id === c)?.nome || "—").split(" ")[0]}`, remover: () => setFiltros((f) => ({ ...f, solicitante: f.solicitante.filter((x) => x !== c) })) })),
    ...(filtros.soTerceiros ? [{ id: "terc", texto: "Com item de terceiro", remover: () => setFiltros((f) => ({ ...f, soTerceiros: false })) }] : []),
    ...(filtros.tipoTerceiro || []).map((t) => ({ id: `tt:${t}`, texto: `Terceiro: ${TIPO_TERCEIRO_LABEL[t] || t}`, remover: () => setFiltros((f) => ({ ...f, tipoTerceiro: f.tipoTerceiro.filter((x) => x !== t) })) })),
    ...(filtros.criadaDe || filtros.criadaAte ? [{ id: "per", texto: `Criada ${filtros.criadaDe ? `de ${formatarDataLocal(filtros.criadaDe)} ` : ""}${filtros.criadaAte ? `até ${formatarDataLocal(filtros.criadaAte)}` : ""}`.trim(), remover: () => setFiltros((f) => ({ ...f, criadaDe: "", criadaAte: "" })) }] : []),
    ...(filtros.prioridade ? [{ id: "prio", texto: `Prioridade: ${filtros.prioridade === "URGENTE" ? "urgente" : "normal"}`, remover: () => setFiltros((f) => ({ ...f, prioridade: "" })) }] : []),
    ...(filtros.soAtrasadas ? [{ id: "atr", texto: "Só atrasadas", remover: () => { setFiltros((f) => ({ ...f, soAtrasadas: false })); setKpiAtivo(null); } }] : []),
    ...(filtros.semAnexo ? [{ id: "anx", texto: "Sem detalhamento", remover: () => { setFiltros((f) => ({ ...f, semAnexo: false })); setKpiAtivo(null); } }] : []),
    ...(filtros.prazoAte ? [{ id: "prz", texto: `Prazo até ${formatarDataLocal(filtros.prazoAte)}`, remover: () => setFiltros((f) => ({ ...f, prazoAte: "" })) }] : []),
    ...(filtros.criadaEm ? [{ id: "cri", texto: `Criada: ${filtros.criadaEm} dias`, remover: () => setFiltros((f) => ({ ...f, criadaEm: "" })) }] : []),
  ];

  function filtrarPorKpi(id) {
    setKpiAtivo(id);
    const base = { ...FILTROS_PADRAO, incluirCanceladas: filtros.incluirCanceladas };
    setFiltros(id ? { ...base, ...(KPI_PARA_FILTRO[id] || {}) } : base);
  }

  function limparFiltros() {
    setFiltros({ ...FILTROS_PADRAO });
    setKpiAtivo(null);
    setBuscaInput("");
  }

  // ------------------------------------------------------------ detalhe
  const carregarDetalhe = useCallback(async (id) => {
    setDetalheLoading(true);
    try {
      setDetalheReq(await obterRequisicao(request, id));
    } catch {
      // Falha transitória num RELOAD não descarta o que já está na tela.
      setDetalheReq((prev) => prev || null);
    } finally {
      setDetalheLoading(false);
    }
  }, [request]);

  function abrirDetalhe(id) {
    setDetalheId(id);
    setDetalheReq(null);
    carregarDetalhe(id);
  }

  function fecharDetalhe() {
    setDetalheId(null);
    setDetalheReq(null);
    if (searchParams.get("abrir")) {
      const novo = new URLSearchParams(searchParams);
      novo.delete("abrir");
      setSearchParams(novo, { replace: true });
    }
  }

  async function recarregarTudo(id) {
    await carregar();
    if (id && detalheId === id) await carregarDetalhe(id);
    if (vista === "linha") carregarEventos();
  }

  // ---------------------------------------------------------- transições
  /** Move pela API; 409 abre o modal certo. Devolve true quando concluiu. */
  async function mover(req, destino, extras = {}) {
    try {
      await moverRequisicaoPara(request, req.id, destino, extras);
      toast.success(`${req.numero} → ${SITUACAO_LABEL[destino] || destino}.`);
      await recarregarTudo(req.id);
      return true;
    } catch (err) {
      const precisa = err?.payload?.precisa;
      if (precisa === "motivo" || precisa === "observacao") {
        setModal({ req, destino, precisa });
        return false;
      }
      if (precisa === "oc") {
        const completa = detalheReq?.id === req.id ? detalheReq : await obterRequisicao(request, req.id).catch(() => null);
        if (completa) setGerarOc({ req: completa, itens: null });
        return false;
      }
      toast.error(err?.payload?.detalhe || err?.message || "Falha na operação.");
      return false;
    }
  }

  async function soltarNaColuna(req, coluna) {
    const destino = destinoDaColuna(coluna.situacoes);
    if (destino && !coluna.situacoes.includes(req.situacao)) {
      const precisa = precisaAntesDeMover(req.situacao, destino);
      if (precisa) { setModal({ req, destino, precisa, etapa: coluna.id }); return; }
      const ok = await mover(req, destino);
      if (!ok) return;
    }
    await moverEtapa(req, coluna.id);
  }

  async function moverEtapa(req, etapa) {
    setTodas((prev) => prev.map((r) => (r.id === req.id ? { ...r, etapa_quadro: etapa } : r)));
    try {
      await moverEtapaRequisicao(request, req.id, etapa);
    } catch {
      toast.error("Não foi possível mover o card.");
      await carregar();
    }
  }

  async function mudarSituacaoItem(item, situacao) {
    if (!detalheReq) return;
    try {
      await marcarSituacaoItem(request, detalheReq.id, item.id, situacao);
      await recarregarTudo(detalheReq.id);
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao atualizar o item.");
    }
  }

  // --------------------------------------------------------------- lote
  function toggleSel(id) {
    setSelecionadas((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  function toggleTodas(ids, marcar) {
    setSelecionadas((prev) => {
      const n = new Set(prev);
      ids.forEach((id) => { if (marcar) n.add(id); else n.delete(id); });
      return n;
    });
  }

  useEffect(() => {
    // Seleção só vale para o que está visível.
    const visiveis = new Set(filtradas.map((r) => r.id));
    setSelecionadas((prev) => {
      const n = new Set(Array.from(prev).filter((id) => visiveis.has(id)));
      return n.size === prev.size ? prev : n;
    });
  }, [filtradas]);

  async function acaoLote(acao, extras = {}) {
    const ids = Array.from(selecionadas);
    if (!ids.length) return;
    try {
      const r = await loteRequisicoes(request, acao, ids, extras);
      if (r?.falhas) {
        const erros = (r.resultados || []).filter((x) => !x.ok).slice(0, 3).map((x) => `${x.numero || x.id}: ${x.erro}`).join(" · ");
        toast.error(`${r.ok} ok; ${r.falhas} falharam. ${erros}`);
      } else {
        toast.success(`${r?.ok ?? ids.length} requisição(ões) atualizada(s).`);
      }
      setSelecionadas(new Set());
      await recarregarTudo();
    } catch (err) {
      if (err?.payload?.precisa === "motivo") { setModal({ lote: "cancelar" }); return; }
      toast.error(err?.payload?.detalhe || err?.message || "Falha na operação em massa.");
    }
  }

  async function exportar(formato) {
    // Exporta TUDO o que o sistema sabe das requisições que estão na tela
    // (filtros e ordem da tabela, do quadro ou do calendário): datas de cada
    // etapa, itens, cotações, OCs, recebimentos e histórico, uma aba por
    // assunto. As colunas escolhidas na tabela são só da tela.
    const naTela = vista === "tabela" ? tabelaNaTela.current : null;
    const ids = idsDasLinhas(naTela?.linhas || filtradas, naTela?.modo);
    if (!ids.length) {
      toast.error("Nada para exportar com os filtros atuais.");
      return;
    }
    setExportando(true);
    try {
      // Import tardio: jsPDF/xlsx pesam e so entram quando alguem exporta.
      const [{ salvarCSV, salvarExcel, salvarPDF }, dados] = await Promise.all([
        import("../../../utils/exportacao"),
        dadosParaExportacao(request, ids),
      ]);
      const { resumo, secoes } = montarExportacaoCompleta(dados);
      const opts = { titulo: "Requisições de Compra — exportação completa", resumo, secoes };
      if (formato === "csv") salvarCSV(opts);
      else if (formato === "pdf") salvarPDF({ ...opts, secoes: fatiarParaPdf(secoes) });
      else salvarExcel(opts);
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao exportar.");
    } finally {
      setExportando(false);
    }
  }

  async function salvarColunas(novas) {
    try {
      const r = await salvarColunasKanbanCompras(request, novas);
      setColunas(r?.colunas || novas);
      setColunasOpen(false);
      toast.success("Colunas salvas.");
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao salvar colunas.");
    }
  }

  // ------------------------------------------------------------- render
  const unidade = todas[0]?.business_unit_codigo ? `${todas[0].business_unit_codigo}${todas[0].business_unit_nome ? ` — ${todas[0].business_unit_nome}` : ""}` : "";
  const cardKanban = (r, _coluna, caixa) => (
    <>
      <KNum>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          {caixa}
          <Mono $size={11} style={{ whiteSpace: "nowrap" }}>{r.numero}</Mono>
          {(r.consolidadas || []).length ? (
            <span title={`Juntou ${r.consolidadas.map((c) => c.numero).join(", ")}`} style={{ color: "var(--ink-4)", fontSize: 10 }}>
              +{r.consolidadas.length}
            </span>
          ) : null}
        </span>
        <TagOrigem origem={r.origem} label={r.origem_label} curta />
      </KNum>
      <KTitle>{r.cliente_nome || resumoSolicitantes(r.solicitantes) || "Sem cliente"}</KTitle>
      <KMeta style={{ display: "block" }}>
        {r.projeto_resumo || (r.data_necessaria ? `Precisa até ${formatarDataLocal(r.data_necessaria).slice(0, 5)}` : "—")}
      </KMeta>
      <BarraItens situacoes={r.itens_situacoes} altura={4} />
      <KMeta>
        <FiClock size={11} style={{ color: r.atrasada ? "var(--danger-text)" : r.dias_para_prazo === 0 ? "var(--warning-text)" : undefined }} />
        <span style={{ color: r.atrasada ? "var(--danger-text)" : undefined }}>{r.prazo_cotacao ? formatarDataLocal(r.prazo_cotacao).slice(0, 5) : "—"}</span>
        <span className="spacer" />
        <span>{r.itens_total} itens</span>
        <Avatar nome={r.comprador_nome} size={22} />
      </KMeta>
    </>
  );

  const conteudo = () => {
    if (loading) return <Carregando linhas={8} colunas={7} />;
    if (erroCarga && todas.length === 0) {
      return <Vazio tone="danger" titulo="Não foi possível carregar a fila" descricao="Tente novamente em instantes." acoes={[{ label: "Tentar de novo", onClick: carregar }]} />;
    }
    if (todas.length === 0 && !busca) {
      return (
        <Vazio
          titulo="Nenhuma requisição ainda"
          descricao="Requisições nascem sozinhas do revisor de ferragens, da separação, dos lotes de produção e do PharuCut — ou você cria uma manual."
          acoes={podeGerir ? [{ label: "Criar requisição manual", onClick: () => setNovaOpen(true) }] : []}
        />
      );
    }
    if (filtradas.length === 0 && vista !== "kanban") {
      return (
        <VazioFiltro
          titulo={busca ? `Nada encontrado para "${busca}"` : "Nada passa nos filtros atuais"}
          descricao={`Com ${nFiltros} filtro${nFiltros === 1 ? "" : "s"} ativo${nFiltros === 1 ? "" : "s"}${!filtros.incluirCanceladas && contagens.canceladas ? ` e ${contagens.canceladas} cancelada(s) oculta(s)` : ""}.`}
          onLimpar={limparFiltros}
        />
      );
    }
    if (mobile) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtradas.map((r) => (
            <button key={r.id} type="button" onClick={() => abrirDetalhe(r.id)} style={{ textAlign: "left", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: 12, display: "flex", flexDirection: "column", gap: 8, font: "inherit", color: "var(--ink)", cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <Mono $size={11}>{r.numero}</Mono>
                <StatusPill situacao={r.situacao} />
              </div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{r.cliente_nome || "Sem cliente"}</div>
              <div style={{ fontSize: 12, color: "var(--ink-3)" }}>{r.projeto_resumo || "—"} · {r.origem_label}</div>
              <BarraItens situacoes={r.itens_situacoes} altura={4} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span>{r.itens_comprados}/{r.itens_total} comprados</span>
                <span style={{ color: r.atrasada ? "var(--danger-text)" : "var(--ink-3)", fontWeight: r.atrasada ? 600 : 400 }}>{rotuloPrazo(r.dias_para_prazo).texto}</span>
              </div>
            </button>
          ))}
        </div>
      );
    }
    if (vista === "tabela") {
      return (
        <TabelaComprador
          requisicoes={filtradas}
          selecionadas={selecionadas}
          onToggleSel={toggleSel}
          onToggleTodas={toggleTodas}
          onAbrir={abrirDetalhe}
          podeGerir={podeGerir}
          versao={versaoDaFila}
          onLinhasVisiveis={guardarTabela}
        />
      );
    }
    if (vista === "kanban") {
      return (
        <Kanban
          requisicoes={filtradas.length || nFiltros || busca ? filtradas : todas}
          colunas={colunas}
          onSoltar={soltarNaColuna}
          onAbrir={abrirDetalhe}
          podeGerir={podeGerir}
          vocab={VOCAB}
          campoItens="itens_total"
          renderCard={cardKanban}
          selecionadas={selecionadas}
          onToggleSel={toggleSel}
          corDoCard={(r, coluna) => (r.atrasada ? "var(--danger-solid)" : coluna.cor)}
          hintColuna={(c) => (c.situacoes?.includes("APROVADA") ? "gera OC" : null)}
        />
      );
    }
    if (vista === "linha") {
      return (
        <LinhaEventos
          eventos={eventos}
          vencemHoje={vencemHoje}
          onAbrir={abrirDetalhe}
          tipo={tipoEvento}
          onTipo={setTipoEvento}
          carregando={eventosLoading}
        />
      );
    }
    return <Calendario requisicoes={filtradas} onAbrir={abrirDetalhe} vocab={VOCAB} eventosDe={eventosDoCalendario} legenda={LEGENDA_CAL} />;
  };

  return (
    <PageContainer className="fila-comprador" style={{ maxWidth: "none", paddingBottom: 96 }}>
      {/* Cabeçalho + KPIs num bloco só (zonas 1 e 2 conectadas, sem vão). */}
      <div>
        <EntityHeader
          title={mobile ? "Requisições" : "Requisições de Compra"}
          subtitle={`${unidade ? `${unidade} · ` : ""}Fila única do comprador: terceiros, ferragem em falta e reposição de estoque.`}
          primaryAction={podeGerir ? { label: "Nova requisição", icon: <FiPlus size={14} />, onClick: () => setNovaOpen(true) } : undefined}
          actions={[{ label: "Exportar", onClick: () => exportar("excel") }]}
          standalone={false}
          sticky={false}
        />
        <KpisFila resumo={resumo} ativo={kpiAtivo} onFiltrar={filtrarPorKpi} />
      </div>

      <FilterBar>
        <SearchWrap>
          <FiSearch size={14} />
          <SearchInput
            ref={buscaRef}
            placeholder="Buscar nº, cliente, projeto, produto…"
            value={buscaInput}
            onChange={(e) => setBuscaInput(e.target.value)}
            aria-label="Buscar requisições"
          />
          <kbd>/</kbd>
        </SearchWrap>
        <button type="button" className="btn btn--secondary" onClick={() => setFiltrosOpen(true)}>
          <FiSliders size={14} /> Filtros {nFiltros ? <Badge>{nFiltros}</Badge> : null}
        </button>
        {chipsAtivos.map((c) => (
          <Chip key={c.id} type="button" $on onClick={c.remover} title="Remover filtro">
            {c.texto} <span className="x"><FiX size={12} /></span>
          </Chip>
        ))}
        <span style={{ flex: 1 }} />
        {!mobile ? (
          <>
            <ExportMenu onExport={exportar} disabled={exportando} label={exportando ? "Exportando…" : "Exportar"} />
            <ViewToggle role="tablist" aria-label="Modo de visualização">
              {VISTAS.map((v) => (
                <ToggleBtn key={v.id} $on={vista === v.id} onClick={() => setVista(v.id)} role="tab" aria-selected={vista === v.id} title={v.label}>
                  <v.icone size={13} /> {v.label}
                </ToggleBtn>
              ))}
            </ViewToggle>
            {podeGerir ? (
              <IconBtn type="button" onClick={() => setColunasOpen(true)} title="Colunas do quadro" aria-label="Colunas do quadro">
                <FiColumns size={15} />
              </IconBtn>
            ) : null}
          </>
        ) : null}
      </FilterBar>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <Chip type="button" $on={!filtros.situacao} onClick={() => { setFiltros((f) => ({ ...f, situacao: "" })); setKpiAtivo(null); }}>
          <span className="dot" /> Ativas <span className="n">{contagens.ativas}</span>
        </Chip>
        {SITUACOES_COMPRA.filter((s) => s.value !== "CANCELADA" || filtros.incluirCanceladas).map((s) => (
          <Chip
            key={s.value}
            type="button"
            $on={filtros.situacao === s.value}
            $cor={getToneTokens(null, s.tone).accent}
            onClick={() => { setFiltros((f) => ({ ...f, situacao: f.situacao === s.value ? "" : s.value })); setKpiAtivo(null); }}
          >
            <span className="dot" /> {s.label} <span className="n">{contagens[s.value] || 0}</span>
          </Chip>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--ink-4)" }}>
          {contagens.ativas} ativas · {contagens.canceladas} canceladas {filtros.incluirCanceladas ? "visíveis" : "ocultas"} · ordenado por prazo
        </span>
      </div>

      {conteudo()}

      {podeGerir && selecionadas.size > 0 ? (
        <BatchBar>
          <Badge style={{ width: 22, height: 22 }}>{selecionadas.size}</Badge>
          <span style={{ fontSize: 13 }}>selecionadas</span>
          <span className="sep" />
          {podeAprovar ? (
            <button type="button" className="btn btn--primary btn--sm" onClick={() => acaoLote("aprovar")}>Aprovar</button>
          ) : null}
          <select
            className="btn btn--secondary btn--sm"
            defaultValue=""
            onChange={(e) => { if (e.target.value) { acaoLote("atribuir", { compradorId: e.target.value === "nenhum" ? null : e.target.value }); e.target.value = ""; } }}
            aria-label="Atribuir comprador"
            style={{ height: 28 }}
          >
            <option value="">Atribuir comprador…</option>
            {pessoas.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            <option value="nenhum">Sem responsável</option>
          </select>
          {podeConsolidar && selecionadas.size >= 2 ? (
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setJuntarOpen(true)}>Juntar…</button>
          ) : null}
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => exportar("excel")}>Exportar</button>
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setModal({ lote: "cancelar" })}>Cancelar…</button>
          <button type="button" className="btn btn--secondary btn--sm" style={{ color: "var(--danger-text)" }} onClick={() => setConfirmExcluir(true)}>
            <FiTrash2 size={12} /> Excluir
          </button>
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setSelecionadas(new Set())} aria-label="Limpar seleção"><FiX size={13} /></button>
        </BatchBar>
      ) : null}

      <Detalhe
        open={!!detalheId}
        req={detalheReq}
        loading={detalheLoading}
        onClose={fecharDetalhe}
        onMover={mover}
        onSituacaoItem={mudarSituacaoItem}
        onRecarregar={() => recarregarTudo(detalheId)}
        onGerarOc={(itens) => detalheReq && setGerarOc({ req: detalheReq, itens })}
        podeGerir={podeGerir}
        podeAprovar={podeAprovar}
        pessoas={pessoas}
      />

      <NovaRequisicao
        open={novaOpen}
        inicial={novaInicial}
        onClose={() => { setNovaOpen(false); setNovaInicial(null); }}
        onCriada={async () => { setNovaOpen(false); toast.success("Requisição criada."); await recarregarTudo(); }}
      />

      <ColunasPanel
        open={colunasOpen}
        colunas={colunas}
        situacoes={SITUACOES_COMPRA}
        padrao={COLUNAS_PADRAO}
        onClose={() => setColunasOpen(false)}
        onSalvar={salvarColunas}
      />

      <FiltrosPanel
        open={filtrosOpen}
        onClose={() => setFiltrosOpen(false)}
        filtros={filtros}
        onAplicar={(f) => { setFiltros(f); setKpiAtivo(null); setFiltrosOpen(false); }}
        pessoas={pessoas}
        solicitantes={solicitantes}
        totalFiltrado={filtradas.length}
        totalGeral={todas.length}
        canceladas={contagens.canceladas}
        meuId={usuario?.id}
      />

      <JuntarModal
        open={juntarOpen}
        requisicoes={todas.filter((r) => selecionadas.has(r.id))}
        onCancelar={() => setJuntarOpen(false)}
        onJuntada={async (principal) => {
          setJuntarOpen(false);
          setSelecionadas(new Set());
          toast.success(`Requisições juntadas em ${principal.numero}.`);
          await recarregarTudo(principal.id);
        }}
      />

      <GerarOcPanel
        open={Boolean(gerarOc)}
        req={gerarOc?.req}
        onClose={() => setGerarOc(null)}
        onGerada={async (oc) => { setGerarOc(null); toast.success(`OC ${oc?.numero || ""} gerada.`); await recarregarTudo(gerarOc?.req?.id); }}
      />

      <ModalMotivo
        open={Boolean(modal)}
        {...(modal?.lote ? { titulo: "Cancelar requisições selecionadas", descricao: `${selecionadas.size} requisição(ões) serão canceladas. Informe o motivo.`, tone: "danger", confirmarLabel: "Cancelar requisições" } : modal ? TEXTO_MODAL(modal.destino, modal.req) : {})}
        onCancelar={() => setModal(null)}
        onConfirmar={async (texto) => {
          if (modal?.lote) { await acaoLote("cancelar", { motivo: texto }); setModal(null); return; }
          const extras = modal.precisa === "observacao" ? { observacao: texto } : { motivo: texto };
          const ok = await mover(modal.req, modal.destino, extras);
          if (ok) {
            if (modal.etapa) await moverEtapa(modal.req, modal.etapa);
            setModal(null);
          }
        }}
      />

      <ConfirmDialog
        open={confirmExcluir}
        titulo={`Excluir ${selecionadas.size} requisição(ões)?`}
        descricao="Só rascunhos, em andamento sem OC e canceladas podem ser excluídas; as demais serão recusadas. Não dá para desfazer."
        confirmarLabel="Excluir"
        onCancelar={() => setConfirmExcluir(false)}
        onConfirmar={async () => { setConfirmExcluir(false); await acaoLote("excluir"); }}
      />
    </PageContainer>
  );
}
